import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, CircularProgress } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { isValidEmail } from '~/modules/helpers';

import { register } from '~/actions';
import { STATUS } from '~/literals';

import { UserProfileFormValidationMessage, UserProfileParams, RootState } from '~/types';
import { config, CONFIG } from '~/config';
import GoogleRecaptchaWidget from '~/components/GoogleRecaptchaWidget';

interface RegisterFormProps {
  onSignInClicked: (event: React.MouseEvent<HTMLElement>) => void;
}

function SignUp({ onSignInClicked }: RegisterFormProps) {
  const dispatch = useDispatch();
  const status = useSelector<RootState, string>(({ user }) => user?.status || STATUS.IDLE);
  const errorMessage = useSelector<RootState, string>(({ user }) => user?.errorMessage || '');

  const [validationMode, setValidationMode] = useState(false);
  const [registerParams, setRegisterParams] = useState<UserProfileParams>({
    email: '',
    password: '',
    subscribe: false,
    firstName: '',
    lastName: '',
    acceptTerms: false,
    captchaToken: null,
  });

  const [validationMessages, setValidationMessages] = useState<UserProfileFormValidationMessage>({
    email: [],
    password: [],
    firstName: [],
    lastName: [],
    acceptTerms: [],
    captchaToken: [],
  });

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...registerParams,
      firstName: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getRegisterValidationMessages(newRegisterParams));
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...registerParams,
      lastName: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getRegisterValidationMessages(newRegisterParams));
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...registerParams,
      email: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getRegisterValidationMessages(newRegisterParams));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...registerParams,
      password: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getRegisterValidationMessages(newRegisterParams));
  };

  const handleSubscribeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...registerParams,
      subscribe: event.target.checked,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getRegisterValidationMessages(newRegisterParams));
  };

  const handleCaptchaVerify = (token: string | null) => {
    const newRegisterParams = {
      ...registerParams,
      captchaToken: token,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getRegisterValidationMessages(newRegisterParams));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const messages = getRegisterValidationMessages(registerParams);
    setValidationMessages(messages);

    // Prevent submit if any validation message exists
    const hasAnyValidationError = Object.values(messages).some(arr => arr.length > 0);
    if (hasAnyValidationError) {
      setValidationMode(true);
    } else {
      dispatch(register({ ...registerParams }));
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      {
        !CONFIG.env.featureFlag.disableUserRegistration ?
          <Box
            sx={{
              marginTop: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign up
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
              {errorMessage ? <Alert sx={{ marginBottom: 2 }} severity="error">{errorMessage}</Alert> : ''}
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <TextField
                    autoComplete="given-name"
                    autoFocus
                    error={validationMode && validationMessages.firstName.length > 0}
                    fullWidth
                    helperText={
                      validationMode && validationMessages.firstName.length > 0
                        ? validationMessages.firstName.join('\n')
                        : null
                    }
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    onChange={handleFirstNameChange}
                    required
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <TextField
                    autoComplete="family-name"
                    error={validationMode && validationMessages.lastName.length > 0}
                    fullWidth
                    helperText={
                      validationMode && validationMessages.lastName.length > 0
                        ? validationMessages.lastName.join('\n')
                        : null
                    }
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    onChange={handleLastNameChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    autoComplete="email"
                    error={validationMode && validationMessages.email.length > 0}
                    fullWidth
                    helperText={
                      validationMode && validationMessages.email.length > 0
                        ? validationMessages.email.join('\n')
                        : null
                    }
                    id="email"
                    label="Email Address"
                    margin="normal"
                    name="email"
                    onChange={handleEmailChange}
                    placeholder="Email Address"
                    required
                    type="email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={validationMode && validationMessages.password.length > 0}
                    fullWidth
                    helperText={
                      validationMode && validationMessages.password.length > 0
                        ? validationMessages.password.join('\n')
                        : null
                    }
                    id="password"
                    label="Password"
                    margin="normal"
                    name="password"
                    onChange={handlePasswordChange}
                    placeholder="Password"
                    required
                    type="password"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        onChange={handleSubscribeChange}
                        value="allowExtraEmails"
                      />
                    }
                    label="I want to receive inspiration, marketing promotions and updates via email."
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={registerParams.acceptTerms}
                        onChange={e => {
                          const newRegisterParams = {
                            ...registerParams,
                            acceptTerms: e.target.checked,
                          } as UserProfileParams;
                          setRegisterParams(newRegisterParams);
                          setValidationMessages(getRegisterValidationMessages(newRegisterParams));
                        }}
                        name="acceptTerms"
                        required
                      />
                    }
                    label={
                      <span>
                        I accept the{' '}
                        <Link href={`${config.app.helpCenterLink}/privacy-policy`} target="_blank" rel="noopener">Privacy Policy</Link>
                        {' '}and{' '}
                        <Link href={`${config.app.helpCenterLink}/terms-of-service`} target="_blank" rel="noopener">Terms & Conditions</Link>.
                      </span>
                    }
                  />
                  {validationMode && validationMessages.acceptTerms && validationMessages.acceptTerms.length > 0 && (
                    <Typography color="error" variant="caption">
                      {validationMessages.acceptTerms.join('\n')}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {/* Use the abstract captcha widget, currently GoogleRecaptchaWidget */}
                  <GoogleRecaptchaWidget onVerify={handleCaptchaVerify} />
                  {validationMode && validationMessages.captchaToken && validationMessages.captchaToken.length > 0 && (
                    <Typography color="error" variant="caption">
                      {validationMessages.captchaToken.join('\n')}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Button
                disabled={status === STATUS.RUNNING}
                fullWidth
                sx={{ mt: 3, mb: 2 }}
                type="submit"
                variant="contained"
              >
                {status === STATUS.RUNNING ? <CircularProgress color="primary" size={24} /> : 'Sign Up'}
              </Button>
              <Grid container justifyContent="flex-end">
                <Grid item>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link onClick={onSignInClicked} sx={{ cursor: 'pointer' }} variant="body2">
                    Already have an account? Sign in
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
          :
          <Typography variant='body1'>User registration not available</Typography>
      }
    </Container>
  );
}

export default SignUp;

const getRegisterValidationMessages = (fields: UserProfileParams): UserProfileFormValidationMessage => {
  const validationMessages: UserProfileFormValidationMessage = {
    password: [],
    email: [],
    firstName: [],
    lastName: [],
    acceptTerms: [],
  };

  if (!fields.password) {
    validationMessages.password.push('* Please choose a password.');
  }

  if (!fields.firstName) {
    validationMessages.firstName.push('* Please enter your first name.');
  }

  if (!fields.lastName) {
    validationMessages.lastName.push('* Please enter your last name.');
  }

  if (!fields.email || !isValidEmail(fields.email)) {
    validationMessages.email.push('* Please enter valid email address.');
  }

  if (fields.acceptTerms === false) {
    validationMessages.acceptTerms?.push('* Please accept the Privacy Policy and Terms & Conditions.');
  }

  // If captcha is required, check if token is provided
  if (CONFIG.env.featureFlag.captchaEnabled && !fields.captchaToken) {
    validationMessages.captchaToken = ['* Please complete the captcha verification.'];
  } else {
    validationMessages.captchaToken = [];
  }

  // Password strength validation
  if (fields.password) {
    const password = fields.password;
    const hasMinLength = password.length >= 8;
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    if (!hasMinLength || !hasNumber || !hasSpecial || !hasLower || !hasUpper) {
      validationMessages.password.push(
        '* Password must be at least 8 characters, include a number, a special character, and a mix of lower and uppercase letters.'
      );
    }
  }

  return validationMessages;
};
